import type { ErrorEvent, EventHint } from '@sentry/angular';

/**
 * Angular's Sentry `ErrorHandler` extracts an `HttpErrorResponse` into its message string before
 * capturing it, so an HTTP status 0 failure surfaces as
 * `"Http failure response for <url>: 0 Unknown Error"` — we have to match on the message rather
 * than on an `HttpErrorResponse` instance.
 *
 * Status 0 means the request never received a response: client offline, navigating away /
 * cancelled request, CORS, or a flaky mobile connection. These are client-environment noise
 * rather than actionable application bugs, so we drop them to keep the Sentry signal clean.
 * The anchors are deliberate: the product-save path prefixes the photo location onto the message,
 * and a status 0 there means an operator's edit never reached the server, so it must still report.
 */
const HTTP_STATUS_ZERO_MESSAGE = /^Http failure response for .*: 0 Unknown Error$/;

/**
 * `hint.originalException` is typed `unknown` because Sentry preserves whatever was passed to
 * `captureException` — a string, an `Error`, an `HttpErrorResponse`, etc. Pull a string message
 * out of either shape so the filter doesn't miss Error-shaped capture paths.
 */
function extractMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (value != null && typeof (value as { message?: unknown }).message === 'string') {
    return (value as { message: string }).message;
  }
  return undefined;
}

function collectEventMessages(event: ErrorEvent, hint: EventHint): Array<string | undefined> {
  return [
    extractMessage(hint.originalException),
    event.message,
    ...(event.exception?.values?.map((value) => value.value) ?? []),
  ];
}

export function isNonActionableHttpNetworkError(event: ErrorEvent, hint: EventHint): boolean {
  const messages = collectEventMessages(event, hint);

  return messages.some((message) => message != null && HTTP_STATUS_ZERO_MESSAGE.test(message));
}

/**
 * clarity.js(GTM 주입 서드파티) 내부 오류. 프레임·스택 표식은 파일 경로라
 * 넓게 보고, 메시지는 'clarity' 홑단어가 아니라 파일·호스트 서명과 대조한다 —
 * 검색어 같은 우리 텍스트가 clarity 를 품으면 버리면 안 되기 때문이다.
 */
const CLARITY_PATH_MARKER = /clarity/i;
const CLARITY_MESSAGE_MARKER = /clarity\.(js|ms)/i;
/** 우리 API 실패는 'Http failure response for <url>: ...' 형태. clarity.js 를 품은 조회 URL 이어도 우리 오류라 버리면 안 된다. */
const OWN_HTTP_FAILURE_MESSAGE = /^Http failure response for /;

function frameFilenames(event: ErrorEvent): string[] {
  const frames = event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];
  return frames.map((frame) => frame.filename ?? '');
}

function originalStackFrames(hint: EventHint): string {
  const stack = (hint.originalException as { stack?: unknown })?.stack;
  if (typeof stack !== 'string') return '';
  // V8 스택 첫 줄은 'ErrorName: <message>'라 메시지가 그대로 박혀 있다.
  // 메시지 대조는 별도 표식으로 하므로 프레임 줄만 본다.
  return stack.split('\n').slice(1).join('\n');
}

export function isThirdPartyClarityError(event: ErrorEvent, hint: EventHint): boolean {
  if (frameFilenames(event).some((filename) => CLARITY_PATH_MARKER.test(filename))) return true;
  if (CLARITY_PATH_MARKER.test(originalStackFrames(hint))) return true;
  return collectEventMessages(event, hint).some(
    (message) => message != null && CLARITY_MESSAGE_MARKER.test(message) && !OWN_HTTP_FAILURE_MESSAGE.test(message),
  );
}

/**
 * PR 프리뷰 API 호스트(api-pr-NNN). production 번들이 프리뷰 API 를 때려
 * production 스트림에 섞인 케이스(3K 429)만 버린다. preview env 자체의
 * API 오류는 그 PR 의 신호이므로 남긴다.
 */
const PREVIEW_API_HOST_MARKER = /api-pr-\d+\./i;

export function isPreviewHostError(event: ErrorEvent, hint: EventHint): boolean {
  if (event.environment === 'preview') return false;
  // captureSurfacedFailure 가 API 요청 URL 을 httpUrl 로 싣는다. extra.url 은 화면 경로라 보지 않는다.
  const httpUrl = (event.extra as { httpUrl?: unknown } | undefined)?.httpUrl;
  if (typeof httpUrl === 'string' && PREVIEW_API_HOST_MARKER.test(httpUrl)) return true;
  return collectEventMessages(event, hint).some((message) => message != null && PREVIEW_API_HOST_MARKER.test(message));
}

/**
 * rxjs `TimeoutError`(role-guard 조회·로그아웃 상한)는 느린 망·서버의 일시 장애라
 * error 가 아니라 warning 으로 내린다. 캡처 지점에서도 내리지만, 새 타임아웃 op이
 * 생겨도 조용히 error 가 되지 않도록 여기서 한 번 더 보장한다.
 */
export function demoteTransientTimeout(event: ErrorEvent, hint: EventHint): void {
  const name = (hint.originalException as { name?: unknown })?.name;
  const types = event.exception?.values?.map((value) => value.type) ?? [];
  if (name === 'TimeoutError' || types.includes('TimeoutError')) {
    event.level = 'warning';
  }
}

/**
 * PR 프리뷰(old-pr-NNN.test)는 빌드 타임 env 가 test/production 그대로라 Sentry 에
 * production 으로 섞인다(3K 429). 호스트명으로 런타임에 분리한다 — 별도 빌드
 * 설정 없이 프리뷰 이슈를 `preview` env 로 모은다.
 */
export function resolveSentryEnvironment(fallback: string, hostname?: string): string {
  const host = hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  return /pr-\d+/i.test(host) ? 'preview' : fallback;
}
